// Interfaces for the Offline Access feature.
// See offline-access-frontend-walkthrough for the backend contract.
//
// Field shape mirrors the existing ticket-registration `/Forms` convention
// (see views/Admin/Forms/ManageForm.tsx): `type` is the numeric `FormType`
// enum, `options` is a string[] (only meaningful for MultiChoice/DropDown),
// and `required` is a boolean.
import { FormType } from "./interfaces-data";

// Every enum is serialized as an integer (confirmed against the live API,
// e.g. GET /OfflineAccess/Setup/1/{resourceId} for a Presentation). The
// walkthrough doc's string values (e.g. "Event") are outdated — ignore them.

export enum OfflineAccessResourceType {
  Event = 0,
  Presentation = 1,
  FileSharing = 2,
}

export enum OfflineAccessRestriction {
  None = 0,
  Whitelist = 1,
  Blacklist = 2,
}

export enum OfflineAccessDownloadOption {
  None = 0,
  Individual_Slide = 1,
  Full_Presentation = 2,
}

export enum OfflineAccessSystemField {
  None = 0,
  FullName = 1,
  Email = 2,
  Phone = 3,
}

export interface OfflineAccessField {
  fieldId?: string;
  label: string;
  type: FormType;
  required: boolean;
  sortOrder: number;
  systemField: OfflineAccessSystemField;
  options: string[];
}

export interface OfflineAccessSetupPayload {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
  teamId: string;
  enabled: boolean;
  publicLink: boolean;
  allowDirectMessage: boolean;
  requireOtp: boolean;
  linkCanExpire: boolean;
  expiresAt?: string | null;
  restriction: OfflineAccessRestriction;
  domains: string[];
  canDownload: OfflineAccessDownloadOption;
  fields: OfflineAccessField[];
}

export interface OfflineAccessResourceSummary {
  title: string;
  friendlyId?: string;
  sessionType?: number;
  eventStatus?: number | null;
  presentationStatus?: number | null;
  ownerId?: string | null;
  ownerName?: string | null;
}

export interface OfflineAccessSetupResponse extends OfflineAccessSetupPayload {
  id?: string;
  resource?: OfflineAccessResourceSummary;
  publicUrl?: string;
}

export interface GetOfflineAccessSetupParams {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
}

export interface OfflineAccessAnswer {
  fieldId: string;
  label: string;
  response: string;
}

export interface OfflineAccessRequestPayload {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
  fullName: string;
  email: string;
  answers?: OfflineAccessAnswer[];
}

export interface OfflineAccessRequestResponse {
  requiresOtp: boolean;
  reference?: string;
  email?: string;
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
  // Present when OTP is not required.
  presentaConnectionId?: string;
  next?: OfflineAccessNext;
}

export interface OfflineAccessVerifyPayload {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
  email: string;
  reference: string;
  otp: string;
}

export interface OfflineAccessNext {
  presentationUrl: string | null;
  eventUrl: string | null;
}

export interface OfflineAccessVerifyResponse {
  requiresOtp: boolean;
  verified: boolean;
  presentaConnectionId: string;
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId?: string | null;
  next?: OfflineAccessNext;
  // Auth block, mirroring the login endpoints (ValidateOTPServiceResponse).
  // Present only when the verified email maps to a real account — the offline
  // access OTP is the same OTP used for login, so a successful verify can log
  // the attendee in. Absent for guests without an account.
  token?: string;
  fullName?: string;
  email?: string;
  phone?: string | null;
  expiresIn?: string;
  roles?: string;
  country?: string;
}

export interface OfflineAccessQuestionsParams {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  presentaConnectionId?: string;
  slideId?: string;
}

export interface OfflineAccessQuestionPayload {
  presentationId: string;
  presentaConnectionId: string;
  slideId: string;
  question: string;
  questionId: string | null;
}

export interface OfflineAccessQuestionAnswerPayload {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  questionId: string;
  answer: string;
}

// Identifies a single question to vote on. The vote endpoints are REST-only —
// the offline page has no SignalR connection, so votes go over HTTP.
export interface OfflineAccessQuestionVoteParams {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  questionId: string;
  presentaConnectionId: string;
}

// Payload for sending (POST) a direct message to the link owner.
export interface OfflineAccessDirectMessagePayload {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  presentaConnectionId: string;
  message: string;
}

// Params for fetching (GET) the direct-message thread. Same path/identity as
// the POST, minus the message body.
export interface OfflineAccessDirectMessageParams {
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  presentaConnectionId: string;
}

// A single direct-message record returned by the GET endpoint.
export interface OfflineAccessDirectMessageItem {
  id: string;
  resourceType: OfflineAccessResourceType;
  resourceId: string;
  eventId: string | null;
  presentaConnectionId: string;
  senderEmail: string;
  senderName: string;
  recipientUserId: string | null;
  recipientEmail: string | null;
  message: string;
  dateSent: string;
}
